import { cn } from "@btnet/utilities-core";

import { BdsFormItem } from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { isTaxGroupIdValid } from "utilities/tax/tax.utilities";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    type ITaxRateSelectProps,
    TaxRateSelect,
} from "commonComponents/financial/TaxRateSelect/TaxRateSelect";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";
import {
    type ITaxRateSelectManagementProps,
    TaxRateSelectManagement,
} from "commonComponents/financial/TaxRateSelect/TaxRateSelectManagement";
import { type ITaxLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { TaxMethod } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

interface ILineItemsProps {
    taxMethodField: string;
    lineItemsField: string;
    taxLineItems: ITaxLineItem[];
}

export interface IDomesticTaxRateSelectProps<FormValues>
    extends Omit<ITaxRateSelectProps<FormValues>, "onChange"> {
    taxGroupId?: number;
    onBlur: (field: string, isTouched?: boolean) => void;
    isReadOnly?: boolean;
    onTaxChange?: (key: string, value?: number) => void;
    setFieldValue: (field: string, value: any) => void;
    lineItemProps?: ILineItemsProps;
    taxGroupIdField?: string;
    label?: string;
    formClassNames?: string[];
    entityName?: string;
    taxRateSelectManagementProps?: ITaxRateSelectManagementProps;
    useBds?: boolean;
}

export const DomesticTaxRateSelect: React.FunctionComponent<
    IDomesticTaxRateSelectProps<undefined>
> = ({
    treeData,
    taxGroupId,
    onBlur,
    isReadOnly,
    onTaxChange,
    setFieldValue,
    lineItemProps,
    taxGroupIdField = "taxGroupId",
    label,
    id,
    "data-testid": testId,
    className,
    formClassNames,
    entityName,
    taxRateSelectManagementProps,
    useBds,
}: IDomesticTaxRateSelectProps<undefined>) => {
    const handleTaxChange = async (key: string, value?: number) => {
        if (lineItemProps) {
            const { taxMethodField, lineItemsField, taxLineItems } = lineItemProps;
            let lineItems = taxLineItems;
            const taxMethod = value === NoTaxId ? TaxMethod.None : TaxMethod.Exclusive;

            let shouldApplyTaxChange = true;

            /**
             * Checking if current tax group (taxGroupId) or newly selected tax group (value) is No Tax,
             * This represents moving from No Tax -> Tax or from Tax -> No Tax; these are the only cases where we should update all lines
             */
            const shouldUpdateAllItems = !taxGroupId || taxGroupId === NoTaxId || value === NoTaxId;

            // Is the user trying to turn off taxes?
            if (entityName && value === NoTaxId) {
                // Make them confirm their choice
                shouldApplyTaxChange = await confirmNoTax(entityName);
            }

            if (shouldApplyTaxChange) {
                if (shouldUpdateAllItems) {
                    // Loop over each line item and set its tax group ID to the selected option
                    lineItems = lineItems.map((l) => ({ ...l, taxGroupId: value }));
                } else {
                    // Loop over each line item and only update those that are taxable
                    lineItems = lineItems.map((l) => {
                        if (isTaxGroupIdValid(l.taxGroupId)) {
                            return { ...l, taxGroupId: value };
                        }
                        return l;
                    });
                }

                // Sync the line items, tax method, and tax group ID with form state
                setFieldValue(lineItemsField, lineItems);
                setFieldValue(taxMethodField, taxMethod);
                setTaxGroupId(key, value);
            }
        } else {
            setTaxGroupId(key, value);
        }
    };

    const confirmNoTax = (entityName: string): Promise<boolean> => {
        return new Promise((resolve) => {
            btConfirm({
                title: `Change the ${entityName} to Non-Taxable?`,
                content: (
                    <>
                        <p>
                            By selecting "No Tax" you will be changing all line items to
                            Non-Taxable.
                        </p>
                        <p>
                            You can reapply a tax rate by selecting a different rate from this menu
                            at any time.
                        </p>
                    </>
                ),
                okText: "Make Non-Taxable",
                okType: "danger",
                onOk: async () => resolve(true),
                onCancel: async () => resolve(false),
            });
        });
    };

    const handleTaxGroupUpdate = (taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[]) => {
        if (taxRateSelectManagementProps && taxRateSelectManagementProps.onTaxGroupUpdate) {
            taxRateSelectManagementProps.onTaxGroupUpdate(taxGroups);
            if (!taxGroups?.find((x) => x.value === taxGroupId)) {
                setTaxGroupId(id, NoTaxId);
            }
        }
    };

    const setTaxGroupId = (key: string, value?: number): void => {
        setFieldValue(taxGroupIdField, value);
        onTaxChange && onTaxChange(key, value);
    };

    const selectManagement = taxRateSelectManagementProps && (
        <TaxRateSelectManagement
            builderId={taxRateSelectManagementProps.builderId}
            jobId={taxRateSelectManagementProps.jobId}
            proposalId={taxRateSelectManagementProps.proposalId}
            showImportOption={taxRateSelectManagementProps.showImportOption}
            onTaxGroupUpdate={handleTaxGroupUpdate}
            taxGroupHandler={taxRateSelectManagementProps.taxGroupHandler}
        />
    );

    const taxSelect = (
        <TaxRateSelect
            id={id}
            data-testid={testId}
            className={cn(["taxDropdown", className])}
            treeData={treeData}
            value={taxGroupId}
            onChange={handleTaxChange}
            onBlur={onBlur}
            readOnly={isReadOnly}
        />
    );

    return useBds ? (
        <BdsFormItem label={label} className={cn(formClassNames)}>
            <BTRow gutter={taxRateSelectManagementProps ? 24 : 0} align="middle">
                <BTCol className="lg-screen-max-width TaxRateSelectCol">{taxSelect}</BTCol>
                {taxRateSelectManagementProps && selectManagement}
            </BTRow>
        </BdsFormItem>
    ) : (
        <BTFormItem label={label} className={cn(formClassNames)}>
            <BTRow gutter={taxRateSelectManagementProps ? 24 : 0} align="middle">
                <BTCol className="lg-screen-max-width TaxRateSelectCol">{taxSelect}</BTCol>
                {taxRateSelectManagementProps && <BTCol>{selectManagement}</BTCol>}
            </BTRow>
        </BTFormItem>
    );
};
